import { QueryClient, hashKey, type QueryKey } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/auth.store';

/**
 * POS-I109: every query is cached under its owner. The hash of a query key is prefixed with
 * `<tenantId>:<userId>` of the signed-in session, so a cached entry written for one user or store
 * can never answer the same key for another, even if a session reset (`lib/session-boundary.ts`)
 * were missed. Partial matching (invalidateQueries / removeQueries with a prefix key) works on the
 * key itself and is unaffected; exact lookups (getQueryData / setQueryData) hash with the owner of
 * the moment, like the hooks that read them.
 */
export function sessionOwnerPrefix(): string {
  const { tenant, user } = useAuthStore.getState();
  return tenant?.id && user?.id ? `${tenant.id}:${user.id}` : 'anonymous';
}

export function sessionQueryKeyHashFn(queryKey: QueryKey): string {
  return `${sessionOwnerPrefix()}|${hashKey(queryKey)}`;
}

/**
 * The app's single React-Query client. It lives in its own module (not inside main.tsx) so the
 * session boundary (`lib/session-boundary.ts`, POS-I109) can cancel and clear it whenever the
 * signed-in user or store changes.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
      refetchOnWindowFocus: false,
      queryKeyHashFn: sessionQueryKeyHashFn,
    },
    mutations: {
      retry: 0,
    },
  },
});
